import { mapDropdownOptionToRHFSelectOption, RHFSelect } from '@talentmesh/ui/components/rhf-select';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { JobDetailsFieldTypes, useJobDetailsForm } from '../JobDetailsStepUtils';
import { JobFunctionsResponse } from '@/api/useJobFunctions';
import { JobCategory } from '@/Models/JobCategory';
import { findJobCategory, mapCategoriesToOptions, mapJobFunctionsToOptions } from '@/Utils/JobFunctionSelectionUtils';
import UIStrings from '@/Utils/UIStrings';

interface Props {
    jobFunctionsMap: JobFunctionsResponse;
    disabled?: boolean;
}

const JobFunctionSelection = ({ jobFunctionsMap: assessmentConfiguration, disabled }: Props) => {
    const { control, formState, resetField } = useJobDetailsForm();
    const jobCategoryId = useWatch({ control, name: 'jobCategoryId' });

    const [jobCategory, setJobCategory] = useState<JobCategory | undefined>(
        findJobCategory(assessmentConfiguration.jobCategories, jobCategoryId),
    );

    useEffect(() => {
        const initialJobCategory = findJobCategory(assessmentConfiguration.jobCategories, jobCategoryId);
        setJobCategory(initialJobCategory);

        if (formState.isDirty) {
            resetField('jobFunctionId');
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobCategoryId]);

    const categoryOptions = useMemo(
        () =>
            mapCategoriesToOptions(assessmentConfiguration.jobCategories.filter((category) => !category.isDeprecated)),
        [assessmentConfiguration.jobCategories],
    );

    const jobFunctionOptions = useMemo(
        () => mapJobFunctionsToOptions(jobCategory?.jobFunctions.filter((func) => !func.isDeprecated) ?? []),
        [jobCategory?.jobFunctions],
    );

    const categorySelectOptions = categoryOptions.map(mapDropdownOptionToRHFSelectOption);
    const jobFunctionSelectOptions = jobFunctionOptions.map(mapDropdownOptionToRHFSelectOption);

    return (
        <>
            <RHFSelect
                label={`${UIStrings.JobCategory}*`}
                name={JobDetailsFieldTypes.JobCategoryId}
                fieldDataCy={JobDetailsFieldTypes.JobCategoryId}
                options={categorySelectOptions}
                disabled={disabled}
            />
            <RHFSelect
                label={`${UIStrings.JobFunction}*`}
                name={JobDetailsFieldTypes.JobFunctionId}
                fieldDataCy={JobDetailsFieldTypes.JobFunctionId}
                options={jobFunctionSelectOptions}
                disabled={jobCategory === undefined || disabled}
            />
        </>
    );
};

export default JobFunctionSelection;
